import React, { useContext, useEffect, useState } from 'react';
import { EditCustomerContext, IEditCustomerContext } from '../../../editCustomerState/editCustomerContext';
import { BpaContext, IBpaContext } from './bpaState/bpaContext';
import { ICustomerBPAForUpdate } from 'types';
import { NEW_BPA_BLOCK_PREFIX, NEW_BPA_TARIFF_PREFIX } from 'appConstants';
import { Form } from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { FormPage } from 'components/EntityForm/FormPage';
import { FormSection } from 'components/EntityForm/FormSection';
import { FormActions } from 'components/EntityForm/FormActions';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import { NoDataNew } from 'components/NoResultsFound/NoDataNew';
import { BpaSet } from './components/BpaSet';
import { ModalWindows } from './components/ModalWindows';
import classNames from 'classnames';

interface IProps {
	isVisible: boolean;
}

export const BpaForm: React.FC<IProps> = ({ isVisible }) => {
	const { customerId } = useContext<IEditCustomerContext>(EditCustomerContext);
	const { changeTypeOfModalWindow, setDataForSubmit, addBpaBlock, bpa } = useContext<IBpaContext>(BpaContext);
	const [allFields, setAllFields] = useState<FieldData[]>([]);
	const [form] = Form.useForm();

	useEffect(() => {
		bpa.forEach((bpaSet) => {
			form.setFieldsValue({
				[`name-${bpaSet.id}`]: bpaSet.name,
				[`recipientAccountNumber-${bpaSet.id}`]: bpaSet.recipientAccountNumber,
				[`recipientName-${bpaSet.id}`]: bpaSet.recipientName,
				[`recipientBank-${bpaSet.id}`]: bpaSet.recipientBank,
				[`recipientBankCity-${bpaSet.id}`]: bpaSet.recipientBankCity,
				[`recipientInn-${bpaSet.id}`]: bpaSet.recipientInn,
				[`recipientBic-${bpaSet.id}`]: bpaSet.recipientBic,
				[`recipientCorrespondentAccount-${bpaSet.id}`]: bpaSet.recipientCorrespondentAccount,
				[`paymentDetails-${bpaSet.id}`]: bpaSet.paymentDetails,
				[`cardContractNumber-${bpaSet.id}`]: bpaSet.cardContractNumber,
				[`sbpContractNumber-${bpaSet.id}`]: bpaSet.sbpContractNumber,
			});

			bpaSet.tariffs.forEach((tariff) => {
				form.setFieldsValue({
					[`typeOfBpaPaymentSystem-${tariff.id}`]: tariff.typeOfBpaPaymentSystem,
				});

				tariff.data.forEach((brand) => {
					form.setFieldsValue({
						[`cardBrand-${brand.id}`]: brand.cardBrand,
						[`bankFee-${brand.id}`]: brand.bankFee,
						[`kmFee-${brand.id}`]: brand.kmFee,
					});
				});
			});
		});
	}, [bpa]);

	const submit = () => {
		const finalBpa: ICustomerBPAForUpdate[] = bpa.map((el) => {
			const isNewBpaSet = el.id.slice(0, NEW_BPA_BLOCK_PREFIX.length) === NEW_BPA_BLOCK_PREFIX;

			const tariffs = el.tariffs.reduce((obj: ICustomerBPAForUpdate['tariffs'], item) => {
				item.data.forEach((tariff) => {
					const isNewTariff = tariff.id.slice(0, NEW_BPA_TARIFF_PREFIX.length) === NEW_BPA_TARIFF_PREFIX;

					obj.push({
						id: isNewTariff ? null : tariff.id,
						bpaPaymentSystem: item.typeOfBpaPaymentSystem,
						cardBrand: tariff.cardBrand,
						bankFee: tariff.bankFee || 0,
						kmFee: tariff.kmFee || 0,
					});
				});
				return obj;
			}, []);

			return {
				id: isNewBpaSet ? null : el.id,
				name: el.name || '',
				recipientAccountNumber: el.recipientAccountNumber || '',
				recipientName: el.recipientName || '',
				recipientBank: el.recipientBank || '',
				recipientBankCity: el.recipientBankCity || '',
				recipientInn: el.recipientInn || '',
				recipientBic: el.recipientBic || '',
				recipientCorrespondentAccount: el.recipientCorrespondentAccount || '',
				paymentDetails: el.paymentDetails || '',
				cardContractNumber: el.cardContractNumber || '',
				sbpContractNumber: el.sbpContractNumber || '',
				tariffs,
			};
		});

		setDataForSubmit({
			customerId,
			bpaSets: finalBpa,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleFieldsChange = (_: FieldData[], fields: FieldData[]) => {
		setAllFields(fields);
	};

	const handleCancel = () => {
		changeTypeOfModalWindow('toConfirmFieldsClearing');
	};

	return (
		<div className={classNames({ hidden: !isVisible })} data-test="BpaForm">
			<FormPage form={form} testId="BpaFormPage" onFinish={submit} onFieldsChange={handleFieldsChange}>
				<FormSection translationKey="bpaSetHeading" testId="BpaSetsSection" isCollapsible={false}>
					<div className="flex flex-col gap-4">
						{bpa.length ? (
							bpa.map((el) => <BpaSet key={el.id} bpaSet={el} allFields={allFields} />)
						) : (
							<NoDataNew />
						)}
						<div>
							<NewButton
								size="small"
								iconLeft={<Icon name={IconName.plus} size="lg" />}
								disabled={bpa.length >= 20}
								testId="AddBpaSetBtn"
								onClick={addBpaBlock}>
								<Text translationNameSpace="form" translationKey="addBpaSetButton" asFragment={true} />
							</NewButton>
						</div>
					</div>
				</FormSection>
				<FormActions onCancel={handleCancel} />
			</FormPage>
			<ModalWindows />
		</div>
	);
};
